import * as React from "react";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import { toast } from "react-toastify";
import { db, storage } from "../../FirebaseConfig";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Input from "@mui/joy/Input";

interface Props {
  eventid: string;
}

const EventParticipantsTable: React.FC<Props> = ({ eventid }) => {
  const [data, setData] = React.useState<any>([]);
  const [submissions, setSubmissions] = React.useState<any>([]);
  const [loading, setLoading] = React.useState(false);
  const [fileLoading, setFileLoading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [uploadFile, setUploadFile] = React.useState<string | null>(null);
  const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);

  const FirestoreTimestampToDate = ({
    seconds,
    nanoseconds,
  }: {
    seconds: any;
    nanoseconds: any;
  }) => {
    const milliseconds = seconds * 1000 + nanoseconds / 1000000;
    const date = new Date(milliseconds);
    const formattedDate = date.toLocaleDateString();
    return formattedDate;
  };

  React.useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const eventDoc = await db.collection("Events").doc(eventid).get();
        const eventData = eventDoc.data();

        if (!eventDoc.exists || !eventData) {
          setLoading(false);
          return;
        }

        const registrationsSnapshot = await db
          .collection("Events")
          .doc(eventid)
          .collection("Registrations")
          .get();

        if (registrationsSnapshot.empty) {
          setLoading(false);
          return;
        }

        const registrations = registrationsSnapshot.docs.map((doc:any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setData(registrations);

        const category = eventData.category;
        const submissionsCollectionName =
          category === "CTF" ? "Submissions" : "Reports";

        const submissionsSnapshot = await db
          .collection("Events")
          .doc(eventid)
          .collection(submissionsCollectionName)
          .get();

        if (!submissionsSnapshot.empty) {
          const fetchedSubmissions = submissionsSnapshot.docs.map((doc:any) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setSubmissions(fetchedSubmissions);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data: ", error);
        setLoading(false);
      }
    };
    getData();
  }, [eventid]);

  const handlePostFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
    registrationId: string
  ) => {
    setFileLoading(true);
    const file = e.target.files?.[0];

    if (!file) {
      setFileLoading(false);
      toast.error("No file selected.");
      return;
    }

    // Check if the file is a valid type (e.g., a VPN configuration file, usually .ovpn)
    const validFileTypes = [
      "application/octet-stream",
      "application/x-openvpn-profile",
    ];
    if (!validFileTypes.includes(file.type)) {
      setFileLoading(false);
      toast.error("Please upload a valid VPN configuration file.");
      return;
    }

    // Get the event name from data
    const eventName = eventid;

    try {
      // Reference to your Firebase Storage bucket
      const storageRef = storage.ref();
      const fileRef = storageRef.child(`Event-files/${eventName}/${file.name}`);

      const uploadTask = fileRef.put(file);

      uploadTask.on(
        "state_changed",
        (snapshot:any) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progress);
        },
        (error:any) => {
          setFileLoading(false);
          toast.error("Upload failed!");
        },
        async () => {
          const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
          setFileLoading(false);
          setUploadFile(downloadURL);
          toast.success("File Uploaded Successfully");

          // & toast success and registration data

          await db
            .collection("Events")
            .doc(eventid)
            .collection("Registrations")
            .doc(registrationId)
            .update({ vpnConfigFileURL: downloadURL });

          toast.success("Registration updated with VPN configuration file URL");
        }
      );
    } catch (error) {
      setFileLoading(false);
      console.error("Error uploading file and updating registration: ", error);
      toast.error("Error & from  registration.");
    }
  };

  const handleClick = () => {
    hiddenFileInput.current?.click();
  };

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
          border: "none",
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "80px",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Sr. No
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                User Name
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Email Id.
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Registration Date
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Submissions
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                VPN Configuration
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((registration: any, index: any) => (
              <tr key={registration.id}>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">{index + 1}</Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">
                    {registration.username ?? "None"}
                  </Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">
                    {registration.useremail ?? "None"}
                  </Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">
                    {FirestoreTimestampToDate(registration.registeredAt)}
                  </Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">
                    <Typography level="body-xs">
                      {
                        submissions.filter(
                          (submission: any) =>
                            submission.submittedBy === registration.useremail
                        ).length
                      }
                    </Typography>
                  </Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">
                    <Input
                      type="file"
                      ref={hiddenFileInput}
                      onChange={(e: any) => handlePostFile(e, registration.id)}
                      style={{ display: "none" }}
                    />
                    <Button
                      startDecorator={<CloudUploadIcon />}
                      color="primary"
                      variant="outlined"
                      onClick={handleClick}
                    >
                      Upload File
                    </Button>
                    <input
                      type="file"
                      onChange={(e) => handlePostFile(e, registration.id)}
                      ref={hiddenFileInput}
                      style={{ display: "none" }} // Make the file input element invisible
                      title="Upload File"
                    />
                  </Typography>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>
    </>
  );
};

export default EventParticipantsTable;
